import type { MaxPopupMenu } from '@maxgraph/core';
import type { Store } from '@/graph-store';
import type { ToastCallbacks } from './ContextMenuService';
import { getEditBlockedMessage } from '@/utils/editBlockedMessages';

export function buildGroupMenu(
  menu: MaxPopupMenu,
  groupId: string,
  store: Store,
  toast?: ToastCallbacks,
): void {
  const group = store.graphData.groups[groupId];
  if (!group) return;

  const isLocked = store.ui.isProtocolLocked || store.ui.isLifecycleLocked || store.ui.isArchived || store.ui.isArchivalInProgress || store.ui.runsExist || store.ui.canEditContent === false;
  const blockedState = {
    isProtocolLocked: store.ui.isProtocolLocked,
    isLifecycleLocked: store.ui.isLifecycleLocked,
    isArchived: store.ui.isArchived,
    isArchivalInProgress: store.ui.isArchivalInProgress,
    runsExist: store.ui.runsExist,
    canEditContent: store.ui.canEditContent,
  };

  // Determine if this is a multi-group action.
  // AC6: right-clicking one of N already-selected groups acts on all N.
  const selectedGroupIds = store.selection.cellIds.filter((id) => id in store.graphData.groups);
  const isMulti = selectedGroupIds.length > 1 && selectedGroupIds.includes(groupId);
  const targetGroupIds = isMulti ? selectedGroupIds : [groupId];

  menu.addItem('Edit Group', '/icon/modify.svg', () => {
    if (isLocked) {
      toast?.showWarning(getEditBlockedMessage(blockedState));
      return;
    }
    store.openModal({
      kind: 'edit-group',
      payload: { groupId },
    });
  });

  if (isMulti) {
    // AC4: show explicit Collapse and Expand items so mixed selections work correctly.
    menu.addItem(
      `Collapse ${targetGroupIds.length} Groups`,
      '/icon/unfold-less.svg',
      () => {
        if (isLocked) {
          toast?.showWarning(getEditBlockedMessage(blockedState));
          return;
        }
        store.setGroupsCollapsed(targetGroupIds, true);
      },
    );
    menu.addItem(
      `Expand ${targetGroupIds.length} Groups`,
      '/icon/unfold-more.svg',
      () => {
        if (isLocked) {
          toast?.showWarning(getEditBlockedMessage(blockedState));
          return;
        }
        store.setGroupsCollapsed(targetGroupIds, false);
      },
    );
  } else {
    menu.addItem(
      group.isCollapsed ? 'Expand' : 'Collapse',
      group.isCollapsed ? '/icon/unfold-more.svg' : '/icon/unfold-less.svg',
      () => {
        if (isLocked) {
          toast?.showWarning(getEditBlockedMessage(blockedState));
          return;
        }
        store.toggleGroupCollapsed(groupId);
      },
    );
  }

  menu.addItem('Ungroup', '/icon/delete.svg', () => {
    if (isLocked) {
      toast?.showWarning(getEditBlockedMessage(blockedState));
      return;
    }
    store.openModal({
      kind: 'confirm-ungroup',
      payload: { groupId },
    });
  });
}

